import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import { sumDecimalStrings } from '@/lib/decimal';
import type {
  RevenueTrackerListParams,
  RevenueTrackerRow,
  RevenueTrackerService,
} from '@/lib/types';

/**
 * The shared revenue reading behind **Revenue by Company** and **Top Movers**.
 *
 * Both tabs answer questions about the same cross-tab — `GET /revenue-tracker`,
 * which returns one row per company and, on each, one cell per cycle. This
 * module fetches it once, and every figure either tab renders comes out of the
 * helpers below rather than out of arithmetic written twice.
 *
 * ## 🔴 THE CURRENCY CONVERTS. IT IS A BASE, NOT A FILTER — owner, 2026-09-18
 *
 * ⚠️ *SUPERSEDED, and it was the premise of this whole module: "**ONE CURRENCY
 * AT A TIME. THIS IS THE WHOLE DESIGN, NOT A FILTER.** Owner's ruling, 11
 * September 2026 … **These two tabs were deliberately left filtering** … ⛔ **Do
 * not carry the conversion across.** Revenue by Product traded the ability to
 * filter to one currency for the ability to add two; that trade was the owner's
 * and it was taken for one tab. … **So the currency is chosen FIRST and
 * everything on screen is that one currency.** Every helper here takes it and
 * filters the underlying service lines to it before summing. … **A company
 * billed in two currencies therefore appears under BOTH**, with a different
 * figure under each."*
 *
 * **The conversion WAS carried across, on the owner's instruction, and the trade
 * was taken for these two tabs as well.** `GET /revenue-tracker` takes a
 * `baseCurrency` and prices every paid line at **its own invoice's `bill_date`**
 * out of `f_exchange_rates` (backend migration 178), so the currency control now
 * asks *"show me the business in pounds"* rather than *"show me the pound part of
 * the business"*.
 *
 * 🔴 **WHAT IT COST, STATED: THE ABILITY TO SEE ONE CURRENCY ALONE HAS GONE.**
 * That was the trade, and it is the same one Revenue by Product took on
 * 2026-09-14. ⛔ **The Google Sheet mirror still FILTERS** — that is the
 * backend's own surface and converting it is its decision, not this module's.
 *
 * 🔴 **WHAT IT BOUGHT: every company is listed.** The old filter dropped
 * **24 of 124 companies under USD**, and most of the estate under GBP — measured
 * on production 2026-09-11, **652 USD invoices against 89 EUR and 56 GBP** — and
 * the shortfall was reported by one sentence under the control. A shorter list is
 * indistinguishable from a quiet year, and that sentence is now deleted because
 * there is no such population left to report.
 *
 * ⚠️ **THE SERVER'S OWN REFUSALS ARE UNCHANGED AND STILL RIGHT.**
 * `meta.grandTotals` is still `null` per cycle on an UNCONVERTED read, and a
 * mixed band total is still withheld, because adding two units is still wrong.
 * What changed is that on a converted read there is only ever one unit to add.
 *
 * ── 🔴 THE ONE WAY THIS GOES WRONG, AND IT GOES WRONG SILENTLY ─────────────
 *
 * **Read the unit off {@link RevenueAnalyticsState.convertedTo} — the server's
 * echo — and NEVER off the picker.** This app deploys separately from the API and
 * can be newer than it: a server predating `baseCurrency` ignores the key and
 * answers an ordinary `200` of unconverted rows. A panel labelling those from its
 * own request state prints `GBP` over dollars, on the two tabs whose figures a
 * finance reader adds up, with every number real, internally consistent, and
 * about a different unit than the word above it. **There is no error to find.**
 *
 * ## ⚠️ WHAT "REVENUE" MEANS HERE, AND WHAT IT DOES NOT
 *
 * - **PAID invoices only**, resolved server-side by the `is_paid` FLAG. Never by
 *   a status name: `f_invoice_statuses` is admin-renameable and the lower case
 *   of *"Unpaid"* contains *"paid"*, a mistake this codebase has shipped three
 *   times. Nothing here re-derives it — the endpoint's population is already
 *   paid-only.
 * - ⚠️ **`is_settled` IS NOT `is_paid`.** Settled is true on **Paid AND
 *   Cancelled**. Anything that reaches for the status flags itself must use
 *   `is_paid` — and it is not exposed on `GET /invoice-statuses`, which is a
 *   second reason to take the population from this endpoint rather than rebuild
 *   it.
 * - **INCLUDING TAX — owner's decision, 2026-10-16.** ⚠️ *SUPERSEDED: "EX-TAX.
 *   The server sums `line_subtotal`, deliberately: VAT is collected on the
 *   government's behalf and is not earned."* Both tabs now read each paid line's
 *   `amountInclTax` (cash collected, VAT included — the figure the Revenue
 *   Tracker table headlines) through {@link paidFigureOf}, which is the ONE
 *   place a line's figure is chosen. Every tile, ranked row, cycle cell,
 *   products × cycles matrix cell, line, CSV and share goes through it, so the
 *   matrix grand total and the row total still agree by construction. The
 *   server converts `tax` at the same rate as `amount`, so the inclusive figure
 *   is in the same base.
 *
 *   ⚠️ Revenue by Product is a DIFFERENT read (`/revenue-tracker/products`) and
 *   decides its own basis; nothing here governs it.
 * - **A cycle is the invoice's BILL DATE falling inside the cycle window**
 *   (`i.bill_date BETWEEN cy.start_date AND cy.end_date`), not the project's
 *   cycle. A whole class of product is never sold through a project, so a
 *   project-derived cycle would leave those lines with no cycle at all.
 */

/** The three currencies actually invoiced in, most-used first (measured 2026-09-11). */
export const REVENUE_CURRENCIES = ['USD', 'EUR', 'GBP'] as const;
export type RevenueCurrency = (typeof REVENUE_CURRENCIES)[number];

/**
 * 🔴 **GBP SINCE 2026-09-18, AND THE ARGUMENT THAT MADE IT USD IS SPENT RATHER
 * THAN OVERRULED.**
 *
 * ⚠️ *SUPERSEDED: "**USD, NOT GBP.** 652 of the 797 invoices are USD; GBP is 56.
 * Opening on GBP would open on 7% of the business and read as an empty
 * dashboard."*
 *
 * **Every clause of that was true, and it was true OF A FILTER.** While the
 * currency narrowed the population, opening on GBP really did open on 7% of the
 * business. These two tabs CONVERT now, so opening on GBP opens on **all** of it
 * restated in pounds — and the reasoning that made USD right is precisely the
 * reasoning that makes it wrong.
 *
 * ⚠️ **BOTH READERS WERE CHECKED BEFORE IT MOVED, AND THERE ARE EXACTLY TWO** —
 * `RevenueByCompanyPanel` and `TopMoversPanel`, both converting as of this
 * change. **Had a still-FILTERING surface read it, the two defaults would have
 * had to part rather than one being dragged**, because moving it would then have
 * opened that surface on 7% of the money, which is the failure this constant was
 * written to prevent.
 *
 * ⚠️ **Revenue by Product keeps its own `DEFAULT_BASE_CURRENCY`, which is also
 * GBP, and the two agreeing is not a reason to merge them.** That tab owns its
 * own URL key and its own parser; one constant three tabs must agree on for ever
 * is the coupling its own doc argues against in the other direction. **They agree
 * today by arriving at the same answer, not by sharing one.**
 *
 * Deliberately NOT `DASHBOARD_CURRENCY` from `@/lib/targets` either — that is the
 * target tiles' default because a TARGET is denominated. Same value, third
 * question; do not collapse them.
 */
export const DEFAULT_REVENUE_CURRENCY: RevenueCurrency = 'GBP';

/** One cycle column, ordered. */
export interface RevenueCycle {
  id: number;
  /** The label `RevenueTrackerRow.cycles` is keyed by, e.g. `2026 - 27`. */
  name: string;
  startDate: string;
  endDate: string;
}

export interface RevenueAnalyticsState {
  rows: RevenueTrackerRow[];
  /**
   * Every cycle, **in date order**.
   *
   * ⚠️ **ORDERED BY `startDate`, NEVER BY `id`.** Measured: id 11 is the
   * earliest cycle (2022-23) and id 1 is the fourth (2025-26). The id sequence
   * has gaps and says nothing about time; sorting by it silently scrambles
   * every column heading and every year-on-year comparison.
   */
  cycles: RevenueCycle[];
  loading: boolean;
  /** The server's own sentence, or null. */
  error: string | null;
  /**
   * Paid lines the server could not place in any cycle — an absent or
   * out-of-range `bill_date`.
   *
   * ⚠️ **Render it when it is above zero.** It is the only thing that can say
   * the columns do not sum to the business's revenue, and a report that quietly
   * omits a year is the kind of error found in front of an accountant. It was
   * `0` on production on 2026-09-11; that is a reading, not a property.
   */
  unmatchedLines: number;
  /**
   * 🔴 **THE UNIT THE SERVER SAYS IT MEASURED IN — `meta.measuredInCurrency`,
   * THE ECHO, NOT THE ONE THAT WAS ASKED FOR.** `null` is *this response was not
   * converted*: a server predating the key (`undefined` on the wire), or a
   * request that sent no base. The two are folded here because both mean *there
   * is no conversion to label*, and neither is a fault.
   *
   * ⛔ **NAMED FOR THE ANSWER RATHER THAN FOR THE QUESTION, DELIBERATELY —
   * exactly as the wire is.** The request key is `baseCurrency` and this is not,
   * because what a caller asked for and what the server measured in are two
   * facts, and the one failure this contract is shaped around is a caller
   * labelling figures from what it sent.
   *
   * ⚠️ **IT IS WHAT EVERY FIGURE ON BOTH TABS IS LABELLED FROM, and what
   * {@link revenueFor} is handed.** See that function: converted, its test stops
   * hiding money and becomes the check that conversion happened.
   */
  convertedTo: string | null;
  /**
   * Paid lines with **no rate stored for their own bill date**, therefore in
   * none of the figures.
   *
   * 🔴 **`null` IS "NOTHING WAS EXAMINED" AND IS NOT `0`.** On the unconverted
   * path the server priced nothing, so it reports `null` rather than a zero that
   * would claim **nothing was lost** about a question nobody asked. ⛔ **A
   * `?? 0` anywhere between the wire and the render turns *we did not look* into
   * *we looked and everything was fine*, silently, on the one field that can say
   * a revenue report is short.** The render decides what an absence looks like;
   * this type is what stops it being decided by accident.
   *
   * ⚠️ **NOT A SECOND COPY OF {@link unmatchedLines}.** One is work that landed
   * in no cycle column; this is work that could not be put into one unit. A line
   * can be neither, either or both, and adding them reports a smaller gap than
   * the report has.
   */
  unpricedLines: number | null;
  reload: () => void;
}

/**
 * Fetches the whole cross-tab, following the pager.
 *
 * ⚠️ **EVERY PAGE, NOT THE FIRST.** 122 companies at the ceiling of 100 per page
 * is two requests. Rendering page one alone would drop 22 companies off a
 * revenue report with nothing on screen saying so — and it would look complete.
 *
 * ⚠️ **`perPage` IS CAPPED AT 100 SERVER-SIDE** and asking for more is a 422,
 * measured. Do not raise it to avoid the second request.
 *
 * ⚠️ **`meta` AND EVERY MEMBER OF IT ARE OPTIONAL** — this app deploys
 * separately from the API and can be newer than it. So no `page` is sent until
 * `meta.totalPages` has actually been seen, and an absent `meta` stops the loop
 * rather than looping for ever.
 *
 * The payload is roughly 250KB a page because every cell carries its service
 * lines. That is the cost of getting the per-line CURRENCY, which is what makes
 * a total exact — the cell totals alone go `null` on a mixed cell and could not
 * be split.
 *
 * @param baseCurrency the unit to CONVERT every figure into, or `null` to ask
 *   for the old unconverted, per-currency lines. ⚠️ **Passing it is a request,
 *   never a guarantee** — read {@link RevenueAnalyticsState.convertedTo} to find
 *   out what actually came back.
 */
export function useRevenueAnalytics(
  baseCurrency: string | null,
  enabled = true,
): RevenueAnalyticsState {
  const [rows, setRows] = useState<RevenueTrackerRow[]>([]);
  const [cycles, setCycles] = useState<RevenueCycle[]>([]);
  const [unmatchedLines, setUnmatched] = useState(0);
  const [convertedTo, setConvertedTo] = useState<string | null>(null);
  const [unpricedLines, setUnpriced] = useState<number | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState(0);

  const reload = useCallback(() => setToken((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        /*
         * 🔴 **THE BASE GOES ON EVERY PAGE, AND THAT IS NOT TIDINESS.** Page two
         * asked without it comes back UNCONVERTED, and those rows are then summed
         * into the same totals as page one's converted ones — dollars added to
         * pounds, in a revenue report, with every figure well-formed and nothing
         * failing. Built once here so the two requests cannot disagree.
         *
         * ⚠️ **Built key by key rather than spread.** The query schema is
         * `.strict()`, so one extra key is a 422 on the only read these two tabs
         * have — and a conditional spread turns OFF excess-property checking,
         * which is how a key nobody declared reaches the wire over a green build.
         */
        const query: RevenueTrackerListParams = { perPage: 100 };
        if (baseCurrency !== null) query.baseCurrency = baseCurrency;

        const collected: RevenueTrackerRow[] = [];
        const first = await api.listRevenueTracker(query, controller.signal);
        collected.push(...first.data);

        const totalPages = first.meta?.totalPages ?? 1;
        for (let page = 2; page <= totalPages; page += 1) {
          const next = await api.listRevenueTracker({ ...query, page }, controller.signal);
          collected.push(...next.data);
        }

        if (cancelled) return;
        setRows(collected);
        setUnmatched(first.meta?.unmatchedLines ?? 0);
        /*
         * 🔴 **READ FROM THE RESPONSE, NEVER SET FROM `baseCurrency`.** These two
         * lines are the whole guard against labelling unconverted money: an older
         * server ignores the key and answers `200`, so the only difference
         * between "measured in GBP" and "answered in whatever it felt like" is
         * this echo being present.
         *
         * ⚠️ **`?? null` rather than `?? baseCurrency`** — the absence is the
         * finding, and defaulting it to what was asked for erases exactly the
         * state this field exists to report. `undefined` (no such key on this
         * server) and `null` (nothing was converted) fold here, and nowhere
         * further down, because both mean *there is no conversion to label*.
         *
         * ⚠️ **OFF THE FIRST PAGE, exactly as `unmatchedLines` and `cycles` are.**
         * Every page carries the same `baseCurrency`, so a per-page echo would be
         * a second answer to one question.
         */
        setConvertedTo(first.meta?.measuredInCurrency ?? null);
        /*
         * ⛔ **`?? null`, AND NEVER `?? 0`.** `null` means the server examined
         * nothing; `0` means it examined everything and lost none of it. On the
         * one field whose job is to say a revenue report is SHORT, coalescing the
         * first into the second is a silent all-clear. The `undefined` of an
         * older server is the same "did not look", so it folds here and nowhere
         * further down.
         *
         * ⛔ **AND IT IS NOT SUMMED ACROSS THE PAGES.** It is a REPORT-WIDE
         * aggregate like `unmatchedLines`, so adding two pages' copies would
         * DOUBLE a count of missing money.
         */
        setUnpriced(first.meta?.unpricedLines ?? null);
        setCycles(
          [...(first.meta?.cycles ?? [])]
            .map((cycle) => ({
              id: cycle.id,
              name: cycle.name,
              startDate: cycle.startDate,
              endDate: cycle.endDate,
            }))
            /*
             * ⚠️ String compare on `YYYY-MM-DD`, which sorts lexicographically
             * and is exact. `new Date()` would attach this browser's offset to a
             * value that has none and can move a boundary date across a day.
             */
            .sort((a, b) => (a.startDate < b.startDate ? -1 : a.startDate > b.startDate ? 1 : 0)),
        );
      } catch (cause) {
        if (cancelled || controller.signal.aborted) return;
        setError(
          cause instanceof ApiError
            ? cause.message
            : 'The revenue figures could not be loaded.',
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
    /* ⚠️ `baseCurrency` is a REQUEST parameter, so it belongs here — it changes
     * what the server computes, not how the answer is read. Leaving it out would
     * leave the figures in whichever base was asked for last, with a control
     * reading the new one above them. */
  }, [enabled, token, baseCurrency]);

  return { rows, cycles, loading, error, unmatchedLines, convertedTo, unpricedLines, reload };
}

/**
 * What one company was paid, in ONE cycle, **in the unit these figures are in**.
 *
 * 🔴 **THE SINGLE GATE EVERY FIGURE ON BOTH TABS PASSES THROUGH.** The ranking,
 * the tiles, the shares, the median, the concentration, the floor, "still
 * trading", every cycle cell and every movement reach the wire through here —
 * which is why conversion was a change to THIS FUNCTION'S MEANING and to nothing
 * downstream: no consumer knows or needs to know whether a rate was applied.
 *
 * 🔴 **`currency` IS THE UNIT THESE FIGURES ARE IN, NOT THE ONE THE READER ASKED
 * FOR.** ⚠️ *SUPERSEDED 2026-09-18, the parameter's meaning and not its type:
 * "in ONE currency … Filtering to one currency first makes the sum exact".*
 * Converted, the server has already restated every line into one base and the
 * caller passes **`meta.measuredInCurrency` — the echo** — so this test stops
 * hiding money and becomes the check that conversion actually happened.
 * Unconverted, the caller passes the picker's value and the old filtering
 * behaviour returns exactly.
 *
 * ⛔ **NEVER PASS THE STRING THAT WAS SENT TO THE SERVER.** A server predating
 * `baseCurrency` ignores it and answers `200` with unconverted rows; comparing
 * against the request would admit USD lines to a GBP total, silently, on a tab
 * whose figures a finance reader adds up. Each panel resolves the unit once —
 * see `figuresCurrency` in `RevenueByCompanyPanel` and `TopMoversPanel`.
 *
 * ⚠️ **THAT A CONVERTED LINE CARRIES THE BASE IS FORCED, NOT ASSUMED.** The
 * server's own `totalFor` withholds a cell total the moment two line currencies
 * disagree, so a conversion that restated the amounts and left the native code on
 * the line would convert the money and still refuse every mixed cell — the whole
 * point of converting, undone. The unit a line states and the unit it is in
 * cannot come apart without the cell total going `null` and saying so.
 *
 * 🔴 **AND THE REASON THE GATE EXISTS AT ALL SURVIVES CONVERSION: THE UNSTATED
 * UNIT.** `d_invoices.currency` is nullable, so a line can arrive with a real
 * amount and `currency: null`. The obvious reading of such a line is *a figure,
 * therefore the unit I am looking at* — money relabelled by assumption. A line
 * with no currency on it cannot be PRICED either, so it is exactly as
 * unadmittable now as it was before; testing the line's own code makes the
 * mistake unreachable rather than guarded against.
 *
 * ⚠️ **Summed from the cell's SERVICE LINES, never from `cell.total`.** The
 * server's own total is `null` whenever a cell mixes units, so reading it would
 * report a real figure as nothing on any unconverted read.
 *
 * ⚠️ **Returns `null` for "nothing in this unit", NOT `"0.00"`.** A company that
 * took nothing and one that took exactly zero are different facts, and every
 * caller renders the first as an empty cell.
 */
export function revenueFor(
  row: RevenueTrackerRow,
  cycleName: string,
  currency: RevenueCurrency,
): string | null {
  const services = row.cycles[cycleName]?.services ?? [];
  const matching = services.filter((service) => service.currency === currency);
  if (matching.length === 0) return null;
  return sumDecimalStrings(matching.map(paidFigureOf));
}

/**
 * The figure ONE paid line contributes to Revenue by Company and Top Movers:
 * cash collected **including tax** (owner, 2026-10-16).
 *
 * 🔴 **THE ONLY PLACE A LINE'S FIGURE IS CHOSEN.** `revenueFor`, the cell
 * projections, the expanded products × cycles matrix and the Lines view all
 * read through here, so a row total and the matrix grand total beneath it can
 * never be summed from two different fields.
 *
 * Same three states the Revenue Tracker table reads `amountInclTax` with:
 * - a string: the server's inclusive figure (VAT converted at the same rate as
 *   the net amount on a converted read).
 * - `undefined`: a server predating the key — the ex-tax `amount`, exactly as
 *   that table falls back.
 * - `null`: a line the server could not price with tax. ⛔ **Never replaced by
 *   the ex-tax `amount`** under a label that says "incl. tax"; it is left out of
 *   every sum, as an unreadable figure always has been (`sumDecimalStrings`
 *   skips `null`).
 */
export function paidFigureOf(service: RevenueTrackerService): string | null {
  return service.amountInclTax === undefined ? service.amount : service.amountInclTax;
}

/** What one company was paid across EVERY cycle, in one currency. */
export function revenueTotalFor(
  row: RevenueTrackerRow,
  cycles: RevenueCycle[],
  currency: RevenueCurrency,
): string | null {
  return sumDecimalStrings(cycles.map((cycle) => revenueFor(row, cycle.name, currency)));
}

/**
 * Which currencies this company's lines actually STATE, in cycle order.
 *
 * 🔴 **IT ANSWERS `[base]` ON EVERY CONVERTED RESPONSE, AND THAT IS THE
 * FUNCTION WORKING RATHER THAN BEING IGNORED.** Converted, every line really is
 * in the base, so the "also paid in EUR" marks its callers draw disappear from
 * the whole table — which is correct: the sibling figure they pointed at has been
 * summed into the one beside them, so the figure is no longer partial and a badge
 * calling it partial would send a reader looking for a second row that does not
 * exist. Same withdrawal, for the same reason, as `alsoSoldIn` on Revenue by
 * Product.
 *
 * ⚠️ **ON AN UNCONVERTED RESPONSE IT IS UNCHANGED AND STILL LOAD-BEARING** — a
 * company billed in two currencies is on the list twice, once under each, nothing
 * adds them, and this is the only mark that says so. So the suppression follows
 * the DATA rather than a flag, which is what keeps it honest against an older
 * server: the badge withdraws itself exactly when the population it describes
 * stops existing, and returns the moment it does not.
 */
export function currenciesUsedBy(row: RevenueTrackerRow): RevenueCurrency[] {
  const seen = new Set<string>();
  for (const cell of Object.values(row.cycles)) {
    for (const service of cell?.services ?? []) seen.add(service.currency);
  }
  return REVENUE_CURRENCIES.filter((code) => seen.has(code));
}

/** Every company with any revenue in this currency, and nothing else. */
export function rowsInCurrency(
  rows: RevenueTrackerRow[],
  cycles: RevenueCycle[],
  currency: RevenueCurrency,
): RevenueTrackerRow[] {
  return rows.filter((row) => revenueTotalFor(row, cycles, currency) !== null);
}

/**
 * The cycle containing a given day, by its own window.
 *
 * ⚠️ **Compared as STRINGS.** `startDate`/`endDate` are plain `YYYY-MM-DD` with
 * no timezone, and that format sorts lexicographically — so a string compare is
 * exact where `new Date()` would introduce an offset the value never had.
 */
export function cycleContaining(cycles: RevenueCycle[], isoDay: string): RevenueCycle | null {
  return cycles.find((cycle) => cycle.startDate <= isoDay && isoDay <= cycle.endDate) ?? null;
}

/**
 * A decimal string with its sign flipped, exactly.
 *
 * String surgery rather than arithmetic, so nothing is parsed to a float on
 * the way to being summed. `"0.00"` is left alone: a negative zero is not a
 * figure anybody should ever see.
 */
function negateDecimal(value: string): string {
  const trimmed = value.trim();
  if (/^-?0(.0+)?$/.test(trimmed)) return trimmed.replace('-', '');
  return trimmed.startsWith('-') ? trimmed.slice(1) : `-${trimmed}`;
}

/** Today, as `YYYY-MM-DD`, on the reader's own clock. */
export function todayIso(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/**
 * The pair of cycles a year-on-year comparison runs over: the one containing
 * today, and the one before it.
 *
 * ⚠️ **THE CURRENT CYCLE IS PARTIAL AND EVERY CALLER MUST SAY SO.** Today is
 * 11 September 2026, five months into `2026 - 27`, which runs to 31 March 2027.
 * Comparing five months against a full twelve makes almost every company look
 * as though it is declining. That is arithmetic, not a finding, and a Top
 * Movers table that does not caption it will be read as a list of lost
 * customers.
 *
 * Returns `null` for either side when there is no such cycle.
 */
export function comparisonCycles(cycles: RevenueCycle[]): {
  current: RevenueCycle | null;
  previous: RevenueCycle | null;
} {
  const current = cycleContaining(cycles, todayIso());
  if (current === null) {
    /*
     * Today falls in no catalogued cycle — reachable, because the cycle list is
     * admin-maintained and can have a gap or end. Fall back to the two most
     * recent cycles rather than rendering nothing.
     */
    const tail = cycles.slice(-2);
    return { current: tail[1] ?? tail[0] ?? null, previous: (tail.length > 1 ? tail[0] : null) ?? null };
  }
  const index = cycles.findIndex((cycle) => cycle.id === current.id);
  return { current, previous: index > 0 ? (cycles[index - 1] ?? null) : null };
}

/**
 * How a company moved between two cycles, in the unit the figures are in.
 *
 * 🔴 **UNDER CONVERSION, PART OF A MOVEMENT CAN BE THE EXCHANGE RATE RATHER
 * THAN THE CUSTOMER — and nothing in this function can separate the two.** Each
 * side is priced at its OWN documents' bill dates, so a company billed in dollars
 * whose dollar revenue did not move at all shows a change in pounds whenever the
 * rate moved between the two cycles. A year-on-year move of a few per cent is
 * ordinary, **652 of 797 invoices are billed in USD** (2026-09-11), and the
 * effect can flip the DIRECTION: three per cent more in dollars against a five
 * per cent adverse rate reads as `down`.
 *
 * ⛔ **IT IS NOT FIXED HERE, AND IT MUST NOT BE.** An FX-neutral change needs the
 * native amounts and the per-line rates, neither of which reaches the browser on
 * a converted response — and re-deriving one from a single rate would be
 * client-side arithmetic across units, which is the thing this whole module
 * refuses. **`TopMoversPanel` says so on screen instead**, in one line gated on
 * the server's echo; see the sentence under its comparison heading.
 *
 * ✅ **WHAT CONVERSION FIXES, ON THE OTHER HAND, IS THE SHARPEST TRAP THIS TAB
 * HAD.** A company that switched from dollars to euros between the two cycles
 * used to read as `stopped` under one currency and `new` under the other — two
 * false findings from one ordinary billing change. Converted, both currencies are
 * summed into the base and the movement is simply the movement.
 *
 * ⚠️ **FIVE OUTCOMES, AND FOUR OF THEM ARE NOT A PERCENTAGE.** The owner asked
 * for every scenario, and collapsing them into one number is what loses the
 * three that matter most commercially:
 *
 * - `new`      nothing before, revenue now — a customer won
 * - `stopped`  revenue before, nothing now — a customer lost, the important one
 * - `up`       both present, more now
 * - `down`     both present, less now
 * - `flat`     both present, identical to the penny
 *
 * ⚠️ **`percent` IS `null` ON `new` AND `stopped`, DELIBERATELY.** Growth from
 * nothing is not "infinite%" and it is not "100%" — it is not a percentage at
 * all, and printing one invents a figure. The same for a total loss. Callers
 * render the WORDS for those two and the number only for up/down/flat.
 */
export type MoverDirection = 'new' | 'stopped' | 'up' | 'down' | 'flat';

export interface Mover {
  row: RevenueTrackerRow;
  /** `null` means nothing in this currency in that cycle. */
  current: string | null;
  previous: string | null;
  direction: MoverDirection;
  /** Signed, unclamped, and `null` wherever a percentage would be a fiction. */
  percent: number | null;
  /** `current - previous`, an exact decimal string; `null` when either side is absent. */
  change: string | null;
}

export function moverFor(
  row: RevenueTrackerRow,
  current: RevenueCycle | null,
  previous: RevenueCycle | null,
  currency: RevenueCurrency,
): Mover | null {
  const now = current === null ? null : revenueFor(row, current.name, currency);
  const before = previous === null ? null : revenueFor(row, previous.name, currency);
  if (now === null && before === null) return null;

  if (before === null) {
    return { row, current: now, previous: null, direction: 'new', percent: null, change: null };
  }
  if (now === null) {
    return { row, current: null, previous: before, direction: 'stopped', percent: null, change: null };
  }

  /*
   * 🔴 **`change` IS COMPUTED EXACTLY, NOT VIA A FLOAT.**
   *
   * *SUPERSEDED: `change: delta.toFixed(2)`, where `delta` was
   * `Number(now) - Number(before)`.* That is a float round trip on a figure the
   * Top Movers table RENDERS — the one thing this codebase forbids of money,
   * and the rule the rest of this module is built on. It happened to be exact
   * at today's magnitudes, which is precisely why it would have survived
   * review and then not survived a larger customer.
   *
   * Negating the earlier side and summing is exact: `sumDecimalStrings` parses
   * to scaled integers and adds in `BigInt`.
   */
  const change = sumDecimalStrings([now, negateDecimal(before)]);

  /*
   * ⚠️ **The float survives for the RATIO and the ORDERING ONLY**, neither of
   * which is a rendered amount. A percentage is a real number by nature and a
   * sort key is never printed.
   */
  const beforeNum = Number(before);
  const delta = Number(now) - beforeNum;
  const direction: MoverDirection = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';
  /*
   * A previous cycle of exactly 0.00 with revenue now is a real state — a
   * refund or a fully discounted line. Dividing by it is an infinity, so the
   * percentage is withheld and the direction still reads.
   */
  const percent = beforeNum === 0 ? null : (delta / beforeNum) * 100;
  return {
    row,
    current: now,
    previous: before,
    direction,
    percent,
    change,
  };
}
